import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  BarChart3,
  Search,
  FileCheck,
  UserCheck,
  PieChart,
  ShieldCheck,
  TrendingUp,
  Target,
  Award,
  Globe,
  Clock,
  ArrowRight,
  ChevronRight,
  CheckCircle2,
  Check,
  Send,
  Building2,
  Sliders,
  Sparkles,
  Lightbulb,
  FileText,
  Activity,
  Layers,
  LineChart,
  Tv,
  Printer,
  Radio,
  Eye,
  DollarSign,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/services_/research-consulting")({
  head: () => ({
    meta: [
      {
        title:
          "Research & Consulting Services — KMWAdvertising LLP | Media Audit, Brand Audit & Campaign Audit",
      },
      {
        name: "description",
        content:
          "Research, Insights & Strategic Consulting That Drive Smarter Marketing Decisions. Evaluate every advertising rupee with independent Media Audits, Brand Audits, and Campaign Audits by KMWAdvertising LLP.",
      },
      {
        property: "og:title",
        content: "Research & Consulting Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Transform marketing guesswork into measurable business growth. Data-driven media rate benchmarking, brand perception analysis, and campaign attribution audits.",
      },
    ],
  }),
  component: ResearchConsultingServicePage,
});

export function ResearchConsultingServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState("Media Audit");

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-emerald-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-emerald-600/20 via-teal-600/15 to-purple-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-sm font-mono font-medium text-slate-300 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-emerald-400 font-semibold">Research & Consulting</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <BarChart3 className="h-3.5 w-3.5" />
              Research & Consulting Services
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Research, Insights & Strategic Consulting That{" "}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-purple-300 bg-clip-text text-transparent">
                Drive Smarter Marketing Decisions
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed mb-8 max-w-3xl">
              <p>
                In today's highly competitive business environment, successful marketing is no longer based on assumptions or intuition. Every strategic decision, whether launching a new brand, planning a media campaign, entering a new market or evaluating marketing performance, should be backed by accurate research, meaningful insights and measurable data.
              </p>
              <p>
                Businesses invest significant budgets in advertising, branding and digital marketing. However, without a structured evaluation of media investments, brand perception and campaign performance, even well-funded initiatives can underperform. Research and consulting provide the clarity needed to optimise marketing spend, reduce risk and maximise return on investment.
              </p>
              <p>
                At KMWAdvertising LLP, we offer comprehensive Research & Consulting Services that combine industry expertise, market intelligence and data-driven analysis to help organisations make informed business decisions. With over 25 years of experience in advertising, media planning, media buying and integrated marketing, we assist businesses in identifying opportunities, improving campaign effectiveness and building stronger brands.
              </p>
              <p>
                Our consulting services cover every stage of the marketing lifecycle, from strategic planning and competitive analysis to media audits, brand audits and campaign performance evaluation.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#audit-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(16,185,129,0.35)] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Schedule a Strategic Audit</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#media-audit"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Media & Brand Audits</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Research & Consulting Matter */}
      <section className="py-16 md:py-24 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-xs font-mono font-bold uppercase tracking-widest text-emerald-400">
              Data-Driven Clarity
            </h2>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white">
              Why Research & Consulting Matter
            </h3>
            <p className="text-slate-300 text-sm sm:text-base">
              Businesses that make decisions based on reliable data consistently outperform those relying on assumptions. Strategic research helps organisations:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              "Understand customer behaviour and market trends",
              "Improve advertising efficiency",
              "Strengthen brand positioning",
              "Reduce marketing waste",
              "Identify growth opportunities",
              "Measure campaign effectiveness",
              "Optimise media investments",
              "Enhance customer experiences",
              "Improve competitive advantage",
              "Support long-term business planning",
            ].map((reason) => (
              <div key={reason} className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 flex items-start gap-3">
                <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-xs text-slate-300 font-medium">{reason}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-xs font-mono text-emerald-300 italic font-semibold">
            Research transforms marketing from guesswork into a measurable business function.
          </p>
        </div>
      </section>

      {/* Media Audit Services */}
      <section id="media-audit" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-mono font-bold uppercase">
              <FileCheck className="h-3.5 w-3.5" />
              Media Audit Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Evaluate Every Advertising Rupee and Improve Media Performance
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Media investments often represent one of the largest components of a company's marketing budget. Without regular evaluation, businesses may overspend on ineffective channels while missing opportunities to improve reach, frequency and return on investment.
              </p>
              <p>
                A Media Audit is a comprehensive assessment of your media planning, buying and campaign execution. It helps ensure that your advertising budget is being allocated efficiently and that media strategies align with business objectives.
              </p>
              <p>
                At KMWAdvertising LLP, we conduct independent, data-driven media audits across traditional and digital platforms.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-white">Our Media Audit Includes:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Media Planning Review</h4>
                <p className="text-xs text-slate-400">We assess whether your media strategy aligns with your target audience, campaign objectives and market conditions.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Media Buying Evaluation</h4>
                <p className="text-xs text-slate-400">We evaluate media rates, negotiated costs, inventory quality, placement effectiveness and buying efficiency to ensure maximum value.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Budget Allocation Analysis</h4>
                <p className="text-xs text-slate-400">We analyse how budgets are distributed across television, print, radio, outdoor, digital, social media, OTT, programmatic and search advertising.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Audience Targeting Assessment</h4>
                <p className="text-xs text-slate-400">We review demographic targeting, geographic reach, behavioural segmentation and audience relevance to determine whether campaigns are reaching the intended consumers.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Media Mix Optimisation</h4>
                <p className="text-xs text-slate-400">Our consultants assess whether the current media mix delivers the best balance between awareness, engagement and conversion.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Performance Benchmarking</h4>
                <p className="text-xs text-slate-400">Campaign performance is compared against industry benchmarks, historical campaigns and competitor activity to identify improvement opportunities.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Vendor & Agency Evaluation</h4>
                <p className="text-xs text-slate-400">We assess media vendors and agency performance to ensure transparency, accountability and cost efficiency.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-emerald-300">Reporting & Recommendations</h4>
                <p className="text-xs text-slate-400">Every media audit concludes with a detailed report highlighting strengths, weaknesses, cost-saving opportunities and actionable recommendations for future campaigns.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Audit Services */}
      <section id="brand-audit" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <UserCheck className="h-3.5 w-3.5" />
              Brand Audit Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Understand How Your Brand Is Perceived in the Marketplace
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                A strong brand is built on consistency, relevance and trust. Over time, changes in customer expectations, market trends and competitive activity can create gaps between how a business intends to be perceived and how customers actually perceive it.
              </p>
              <p>
                A Brand Audit evaluates every aspect of your brand to identify opportunities for improvement and ensure your identity remains aligned with your business objectives.
              </p>
            </div>
          </div>

          <div className="space-y-8 mb-12">
            <h3 className="text-xl font-bold text-white">Our Brand Audit examines:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Brand Positioning</h4>
                <p className="text-xs text-slate-400">We evaluate how your brand is positioned relative to competitors and whether it occupies a clear and differentiated space in the market.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Brand Identity</h4>
                <p className="text-xs text-slate-400">Our consultants review your logo, visual identity, typography, colour palette, imagery, design consistency and brand guidelines.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Brand Messaging</h4>
                <p className="text-xs text-slate-400">We assess the effectiveness of your brand story, tone of voice, value proposition and communication strategy across all marketing channels.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Customer Perception</h4>
                <p className="text-xs text-slate-400">Through research and analysis, we identify how customers perceive your brand and compare those perceptions with your intended positioning.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Competitive Analysis</h4>
                <p className="text-xs text-slate-400">We benchmark your brand against key competitors to identify strengths, weaknesses and opportunities for differentiation.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Digital Brand Presence</h4>
                <p className="text-xs text-slate-400">We evaluate your website, social media, online reviews, search visibility, content strategy and overall digital reputation.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Internal Brand Alignment</h4>
                <p className="text-xs text-slate-400">Strong brands begin internally. We assess whether employees understand and consistently represent your brand values and culture.</p>
              </div>

              <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white text-purple-300">Brand Consistency</h4>
                <p className="text-xs text-slate-400">We review advertising, packaging, marketing materials, presentations, websites and customer communications to ensure consistent brand execution.</p>
              </div>
            </div>
          </div>

          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
            <h3 className="text-xl font-bold text-white">Benefits of a Brand Audit</h3>
            <p className="text-xs text-slate-400">A comprehensive brand audit helps businesses:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-xs text-slate-300">
              {[
                "Strengthen brand recognition",
                "Improve customer trust",
                "Enhance visual consistency",
                "Identify communication gaps",
                "Differentiate from competitors",
                "Improve customer loyalty",
                "Support rebranding initiatives",
                "Align internal and external brand perception",
                "Improve marketing effectiveness",
                "Increase long-term brand value",
              ].map((benefit) => (
                <div key={benefit} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Audit Services */}
      <section id="campaign-audit" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-300 text-xs font-mono font-bold uppercase">
              <PieChart className="h-3.5 w-3.5" />
              Campaign Audit Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Measure Campaign Performance Beyond Basic Metrics
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Advertising campaigns generate large volumes of data, but numbers alone rarely provide the full picture. A Campaign Audit evaluates whether your campaign achieved its objectives and identifies opportunities to improve future performance.
              </p>
              <p>
                Our campaign evaluation combines quantitative data with qualitative analysis to provide a complete understanding of campaign effectiveness.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Strategic Objective Review</h3>
              <p className="text-xs text-slate-400">We assess whether campaign objectives were clearly defined and aligned with broader business goals.</p>
            </div>

            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Creative Evaluation</h3>
              <p className="text-xs text-slate-400">We analyse messaging, visual design, storytelling, calls-to-action and creative consistency across all channels.</p>
            </div>
          </div>

          {/* Media Performance Analysis */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4 mb-8">
            <h3 className="text-xl font-bold text-white">Media Performance Analysis</h3>
            <p className="text-xs text-slate-400">Campaign performance is evaluated across:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs text-slate-300">
              {[
                "Television",
                "Print",
                "Radio",
                "Outdoor",
                "Google Ads",
                "Meta Ads",
                "LinkedIn Advertising",
                "Programmatic",
                "OTT",
                "YouTube",
                "Social Media",
                "Email Marketing",
              ].map((ch) => (
                <div key={ch} className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-center">
                  {ch}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Audience Effectiveness</h3>
              <p className="text-xs text-slate-400">We review audience targeting, segmentation and engagement to determine whether campaigns reached the intended consumers.</p>
            </div>

            {/* Performance Metrics */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-lg font-bold text-white text-teal-300">Performance Metrics</h3>
              <p className="text-xs text-slate-400">Our consultants analyse key performance indicators including:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-slate-300">
                {[
                  "Reach",
                  "Impressions",
                  "Frequency",
                  "Engagement Rate",
                  "Click-Through Rate (CTR)",
                  "Conversion Rate",
                  "Cost per Click (CPC)",
                  "Cost per Acquisition (CPA)",
                  "Return on Ad Spend (ROAS)",
                  "Marketing ROI",
                ].map((metric) => (
                  <div key={metric} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-teal-400 shrink-0" />
                    <span>{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Customer Journey Analysis</h3>
              <p className="text-xs text-slate-400">We evaluate how users interacted with the campaign across different touchpoints and identify opportunities to improve conversion paths.</p>
            </div>

            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Attribution Analysis</h3>
              <p className="text-xs text-slate-400">Understanding which channels contributed most to campaign success enables more effective future media planning and budget allocation.</p>
            </div>

            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-lg font-bold text-white text-teal-300">Recommendations</h3>
              <p className="text-xs text-slate-400">Every campaign audit concludes with practical recommendations to improve creative performance, media strategy, targeting and conversion outcomes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation & Lead Form */}
      <section id="audit-form" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-emerald-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-emerald-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                Actionable Data-Driven Insights
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Make Better Marketing Decisions with Research-Driven Insights
              </h2>
              <div className="space-y-2 text-slate-300 text-sm">
                <p>
                  Whether you're evaluating your media investments, assessing your brand's market position or measuring campaign effectiveness, informed decisions begin with reliable research.
                </p>
                <p>
                  At KMWAdvertising LLP, we help businesses uncover opportunities, eliminate inefficiencies and build smarter marketing strategies through comprehensive research and consulting services.
                </p>
                <p className="font-semibold text-emerald-300">
                  Contact our consulting team today to schedule a Media Audit, Brand Audit or Campaign Audit and discover how data-driven insights can transform your marketing performance.
                </p>
              </div>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Audit Request Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our senior consulting director will reach out within 24 hours to schedule your audit briefing session.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ananya Roy"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Apex Enterprises"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Select Audit Type *</label>
                  <select
                    value={selectedAudit}
                    onChange={(e) => setSelectedAudit(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-emerald-500 transition"
                  >
                    <option value="Media Audit">Media Audit (Evaluate Media Spend & Rates)</option>
                    <option value="Brand Audit">Brand Audit (Perception & Positioning Review)</option>
                    <option value="Campaign Audit">Campaign Audit (Attribution & Conversion Review)</option>
                    <option value="Comprehensive Audit">Comprehensive 360° Marketing Audit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Brief / Key Objectives</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your current ad spend, media channels, or specific audit requirements..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Strategic Audit Consultation</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
